import type {
  FleetUser,
  OperationalSituation,
  VehicleConsumptionGoal,
  VehicleDriverAuthorizationStatus,
  VehicleDto,
  VehicleFragment,
  VehicleOdometer,
  VehicleTag,
} from '@cobliteam/api-lib/src/__generated__/graphql';

import type { User } from '@cobliteam/common-types';
import type { UUID } from './identifier';

export enum VehicleType {
  // General
  CAR = 'CAR',
  VAN = 'VAN',
  TRUCK = 'TRUCK',
  MOTORCYCLE = 'MOTORCYCLE',
  BUS = 'BUS',
  MICRO_BUS = 'MICRO_BUS',
  PICKUP = 'PICKUP',

  // Services
  AMBULANCE = 'AMBULANCE',
  FIRE_TRUCK = 'FIRE_TRUCK',
  TAXI = 'TAXI',
  GARBAGE_TRUCK = 'GARBAGE_TRUCK',

  // Water
  BOAT = 'BOAT',

  // Building
  CRANE = 'CRANE',

  // Airport
  TRACTOR = 'TRACTOR',
  TUG = 'TUG',
  LOADER = 'LOADER',
  LADDER = 'LADDER',
  GPU_APU = 'GPU_APU',
  CONVEYOR = 'CONVEYOR',
  FORK_LIFT = 'FORK_LIFT',

  OTHER = 'OTHER',
}

export enum VehicleDriverAuthorizationMode {
  AllowAnyFleetDriver = 'ALLOW_ANY_FLEET_DRIVER',
  AllowChosenDriver = 'ALLOW_CHOSEN_DRIVER',
  Disabled = 'DISABLED',
}

export interface VehicleDriver {
  id: string;
  name: string;
  phoneNumbers: string[];
}

export interface VehicleLastLocation {
  isPlugged?: boolean;
  timeInMillis?: number;
  cobliIngestedAt?: number;
}
export interface VehicleModel {
  type?: VehicleType;
  brand?: string;
  name?: string;
  year?: string;
  version?: string;
  isSuggestion?: boolean;
}

export interface Fuel {
  type?: string;
  price?: number;
  consumption?: number;
}

export interface Vehicle {
  id: string;
  deviceId?: string;
  deviceType?: string;
  deviceGeneralType?: string;
  deviceReportsVideo?: boolean;
  deviceModel?: string;
  cobliId?: string;
  licensePlate?: string;
  alias?: string;
  driver?: VehicleDriver;
  driverId?: string;
  newDriver?: VehicleDriver;
  oldDriver?: VehicleDriver;
  model: VehicleModel;
  label: string;
  fleetId: string;
  tagId?: string;
  fuel?: Fuel;
  size?: string;
  fuelConsumption?: number;
  groups?: VehicleGroup[];
  speedLimitInKmh?: number;
  odometer?: VehicleOdometer;
  lastLocation?: VehicleLastLocation;
  disablerStatus?: VehicleDisablerStatus;
  isAllowListEnabled?: boolean;
  isImmobilizerEnabled?: boolean;
  vehicleDriverAuthorizationMode?: VehicleDriverAuthorizationMode;
  tags?: VehicleTag[];
  groupPath?: string;
  immobilizerStatus?: VehicleDriverAuthorizationStatus;
  consumptionGoal?: VehicleConsumptionGoal;
  operationalSituation?: VehicleOperationalSituation | null;
}

/**
 * Situação operacional como a tabela de veículos consome — derivada do tipo gerado com Pick, então
 * nada aqui é escrito à mão e não pode divergir do schema.
 *
 * Não usa o tipo gerado inteiro porque ele aninha `createdBy: FleetUser` e a union `target`, e
 * `Vehicle` é montado via `camelcaseKeys(..., { deep: true })` em vários transformers (camera monta
 * `Driver.vehicle`, por exemplo) — o tipo que o camelcaseKeys produz não casa com tipo gerado
 * aninhado, e apontar para o tipo cheio propagaria essa exigência para todos eles.
 */
export type VehicleOperationalSituation = Pick<OperationalSituation, 'id' | 'situation' | 'startsAt' | 'endsAt' | 'reason' | 'active'>;

export interface VehicleGroup {
  id: UUID;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  creatorId?: UUID;
  creator?: User;
  vehicleIds?: UUID[];
  userIds?: UUID[];
  path?: string;
}

export interface VehicleGroupWithPath {
  id: UUID;
  name: string;
  path: string;
}

export interface VehicleGroupWithUsers {
  id: UUID;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  creatorId?: UUID;
  creator?: FleetUser;
  path?: string;
  vehicles?: VehicleDto[];
  users?: FleetUser[];
}

export interface NewVehicleGroup {
  name: string;
  vehicleIds: UUID[];
  userIds: UUID[];
}

export enum VehicleSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  BIG = 'big',
}

export enum VehicleStatus {
  ON = 'on',
  OFF = 'off',
  DISABLED = 'disabled',
  DISABLE_PENDING = 'disable_pending',
  ENABLE_PENDING = 'enable_pending',
  REMOVED = 'removed',
  IN_DEVOLUTION = 'in_devolution',
}

export interface VehicleDisablerStatus {
  status?: string;
  lastDisablingAction?: string;
  lastDisablingActionTime?: number;
}

export enum MultiSelectVehicleAssociationType {
  MOVING_WITHOUT_VEHICLE = 'Perdendo dados',
  ASSOCIATED_TO_VEHICLE = 'Associado',
  NOT_ASSOCIATED_TO_VEHICLE = 'Desassociado',
}

export const getVehicleLabel = (licensePlate?: string, model?: string, brand?: string, cobliId?: string, alias?: string | null) => {
  const label = `${licensePlate || ''} ${brand || ''} ${model || ''}`.trim() || cobliId || 'Sem dispositivo';
  return alias ? `${label} (${alias})` : label;
};

/**
 * Reduz um termo ao que o usuário de fato digita: sem acento, sem caixa e sem pontuação.
 * Assim "Ônibus" acha com "onibus" e a placa "ABC-1D23" acha com "abc1d23".
 *
 * O espaço cai junto da pontuação porque `matchesVehicleSearch` já quebra a busca em termos
 * antes de normalizar — cada termo aqui é uma palavra só.
 */
export const normalizeVehicleSearchTerm = (value?: string | null) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Structural shape shared by `Vehicle`, `VehicleDTO` GraphQL fragments and device-nested vehicles. */
interface VehicleSearchableVehicle {
  licensePlate?: string | null;
  alias?: string | null;
  cobliId?: string | null;
  model?: { name?: string | null; brand?: string | null } | null;
  driver?: { name?: string | null } | null;
  groups?: readonly { name: string }[] | null;
}

interface MatchesVehicleSearchOptions {
  /** Also match the assigned driver's name. Off by default — opt in where the field advertises it. */
  includeDriver?: boolean;
  /** Also match the vehicle's group names. Off by default — opt in where the field advertises it. */
  includeGroups?: boolean;
}

/**
 * Fields a vehicle text search matches by default: plate, alias (apelido), Cobli id and model/brand.
 * Alias is part of the default set because many fleets use it as the vehicle's primary identifier.
 *
 * Only raw vehicle fields belong here — never a composed `label`. A label is free-form (routing's,
 * for one, appends the driver's name), so including it would silently match fields that
 * `includeDriver`/`includeGroups` are meant to gate. Everything a label composes is already listed
 * individually below.
 */
export const getVehicleSearchableValues = (vehicle: VehicleSearchableVehicle, { includeDriver, includeGroups }: MatchesVehicleSearchOptions = {}): string[] => [
  vehicle.licensePlate ?? '',
  vehicle.alias ?? '',
  vehicle.cobliId ?? '',
  vehicle.model?.name ?? '',
  vehicle.model?.brand ?? '',
  ...(includeDriver ? [vehicle.driver?.name ?? ''] : []),
  ...(includeGroups ? (vehicle.groups ?? []).map((group) => group.name) : []),
];

/**
 * Diacritic- and punctuation-insensitive match of `query` against the vehicle's searchable values.
 *
 * The query is split on whitespace and **every** term must match **some** field. Matching term by
 * term (instead of the whole query against a single field) is what makes "mercedes sprinter" find a
 * Mercedes-Benz Sprinter, since brand and model are separate fields — and it keeps the order free.
 * Because punctuation is dropped per term, "ABC 1D23" and "ABC-1D23" both find the plate `ABC1D23`.
 *
 * An empty or whitespace-only query means "no filter" (matches everything). A query with content but
 * no alphanumerics ("-", ".") is a real filter that nothing can satisfy, so it matches nothing.
 */
export const matchesVehicleSearch = (vehicle: VehicleSearchableVehicle, query?: string | null, options: MatchesVehicleSearchOptions = {}): boolean => {
  const rawTerms = (query ?? '').split(/\s+/).filter(Boolean);
  if (rawTerms.length === 0) return true;

  const terms = rawTerms.map(normalizeVehicleSearchTerm).filter(Boolean);
  if (terms.length === 0) return false;

  const values = getVehicleSearchableValues(vehicle, options).map(normalizeVehicleSearchTerm).filter(Boolean);

  return terms.every((term) => values.some((value) => value.includes(term)));
};

export const getLicensePlateOrCobliIdOrPlaceholder = (vehicle: Vehicle) => {
  return vehicle.licensePlate || vehicle.cobliId || 'Sem dispositivo';
};

export const getVehicleGroupsLabel = (vehicle: Vehicle | VehicleFragment | undefined | null) => {
  if (!vehicle?.groups || vehicle.groups.length === 0) return 'Sem grupos cadastrados';

  return vehicle.groups.map((group) => group.name).join(', ');
};

export const parseVehicleSize = (size?: VehicleSize) => {
  switch (size?.toLowerCase()) {
    case VehicleSize.SMALL:
      return 'Pequeno';
    case VehicleSize.MEDIUM:
      return 'Médio';
    case VehicleSize.BIG:
      return 'Grande';
    default:
      return '---';
  }
};

/**
 * Resolves any porte representation to the {@link VehicleSize} enum, or undefined when there is
 * no defined porte. Needed because the app-layer `Vehicle.size` carries the pt-BR label
 * ("Pequeno"/"Médio"/"Grande" — the GraphQL transform maps the API enum through
 * `vehicleSizeAPIToISize`), while feature logic compares against the enum values — comparing the
 * raw string against the enum silently matches nothing.
 */
export const normalizeVehicleSize = (size?: string): VehicleSize | undefined => {
  switch (size?.trim().toLowerCase()) {
    case VehicleSize.SMALL:
    case 'pequeno':
      return VehicleSize.SMALL;
    case VehicleSize.MEDIUM:
    case 'médio':
    case 'medio':
      return VehicleSize.MEDIUM;
    case VehicleSize.BIG:
    case 'grande':
      return VehicleSize.BIG;
    default:
      return undefined;
  }
};
